/**
 * SSML チューニング画面の固定値 (計画 8.4.2)。
 *
 * ⚠️ 「音声は Amazon Polly / 話者 Takumi / ja-JP 固定」は MVP 期間中の確定事項
 * (計画 8 章)。Provider 選択 / Voice 選択は **作らない**。
 * 話者・言語の値は `web-app/src/data/pollyVoices.ts` を参照し、
 * 本ファイルでハードコードしない (正源を 1 つに保つ)。
 */

import { DEFAULT_LANGUAGE_KEY, DEFAULT_POLLY_VOICE_ID } from '../../data/pollyVoices';

/**
 * 放送テキストの長さ上限 (文字)。
 *
 * サーバ側 (`lambda/common/ssml_tuning.py` の `MAX_TUNING_TEXT_LENGTH`) と
 * **同じ値** にする。画面側で先に弾くのは、送信して 400 が返るより
 * 「入力中に気づける」方が親切だからで、サーバ側の検証を省く意味ではない。
 *
 * ⚠️ サーバ側を変えたらここも変えること (値がずれると
 * 「画面では入力できるのに解析だけ 400」という分かりにくい状態になる)。
 */
export const MAX_TUNING_TEXT_LENGTH = 3000;

/**
 * 「リセット」ボタンの表示名 (Step1 タイトル行の右側)。
 *
 * ⚠️ Step1 だけでなく **Step1〜Step3 の全体** を初期化するボタン。
 * 駅名・路線名・定型文などを連続で検証する際に、前回の内容を
 * 手で消す手間と「前回の結果との混在」を無くすために置く。
 */
export const RESET_BUTTON_LABEL = 'リセット';

/**
 * 「リセット」押下時の確認メッセージ (`\n` で改行 / `ConfirmDialog` に渡す)。
 *
 * ⚠️ リセット対象は **画面上の作業内容のみ**。README・サーバ側データ・
 * マスタデータ等の永続データには一切触れない (API も呼ばない)。
 */
export const RESET_CONFIRM_MESSAGE = '現在の検証内容をすべてクリアします。\nよろしいですか？';
/**
 * Audio サンプルの候補数の **上限** (計画 9.2)。
 *
 * サーバ側 (`lambda/common/ssml_tuning.py` の `SAMPLE_CANDIDATE_COUNT`) と
 * **同じ値**。画面では見出しと説明文の「最大 5 件」表記に使う。
 *
 * ## ⚠️ 「必ず 5 件」ではない
 *
 * 実際に返る候補は **これより少ないことがある**。
 *
 *   - AI が狙いを作り分けられず 3〜4 件に絞った (**正常な挙動** / 計画 9.2)
 *   - AI 応答が出力トークン上限で切れた (計画リスク R5)
 *
 * そのため描画では必ず `candidates.length` を使い、本定数で
 * 固定長の配列アクセスをしてはいけない (3 件のとき空カードが出る / R19)。
 * 定数名に `MAX_` を付けているのは、後任が「5 件固定」と誤解して
 * `candidates[4]` のような実装を書くのを防ぐため。
 */
export const MAX_SAMPLE_CANDIDATE_COUNT = 5;

/**
 * SSML チューニングの音声合成に使う Polly VoiceId (**Takumi 固定**)。
 *
 * ## ⚠️ 選択させない
 *
 * 要件で「Amazon Polly 固定 / Takumi 固定」と決まっており、
 * Provider 選択・Voice 選択の UI は **作らない** (計画 8 章の確定前提)。
 * 聴き比べたいのは *話者の違い* ではなく *SSML の違い* なので、
 * 話者を可変にすると比較の軸が 2 つになり判断できなくなる。
 *
 * 値は `web-app/src/data/pollyVoices.ts` の {@link DEFAULT_POLLY_VOICE_ID}
 * (= `Takumi`) を参照する。文字列リテラルを書かないのは、
 * 既定話者が変わったときに 2 箇所直す必要をなくすため (単一正源)。
 */
export const SSML_TUNING_VOICE_ID = DEFAULT_POLLY_VOICE_ID;

/**
 * SSML チューニングの音声合成に使う言語 (`ja` 固定)。
 *
 * `POST /audio-preview` の `language` に渡すと、サーバ側の言語マスタから
 * `LanguageCode` / `Engine` (Neural) / 既定話者が解決される。
 * 車内放送の日本語文面をチューニングする機能なので、多言語は対象外
 * (多言語の試聴は放送地点マスタ側の機能が担う)。
 */
export const SSML_TUNING_LANGUAGE_KEY = DEFAULT_LANGUAGE_KEY;

/**
 * 「Audio サンプル作成」ボタンの名称 (UI/UX 改善 Phase4)。
 *
 * Step2 の「改善案を試聴」(SSML なし) と区別するため、
 * **SSML タグでチューニングした** 音声を作ることを名称に含める。
 * ⚠️ 押せない理由・案内文もこの定数から組み立てる (名称変更時の書き漏れを防ぐ)。
 */
export const CREATE_SAMPLES_BUTTON_LABEL = 'SSMLタグでチューニングしたAudioサンプル作成';

/*
 * ## Step2 / Step3 の責務を画面から読み取れるようにする文言
 *
 * SBI-001: 読み上げ要望・AI 改善案・改善理由を画面から廃止した。
 * Step2 = Step1 の放送テキストの確認 (暫定 / 後続 SBI で辞書適用済 SSML の確認・編集へ置き換える)。
 * Step3 = 放送テキストの SSML チューニング・比較試聴。
 */

/** Step2 の見出し (h3 のアクセシブルネームになる)。SBI-003 で辞書適用済 SSML の確認へ変更。 */
export const STEP2_TITLE = 'Step2 辞書適用済SSMLの確認';

/**
 * 「辞書適用Audio作成」ボタンの名称 (SBI-003 / Step1 の放送テキストから Step2 を生成する)。
 *
 * SSML 辞書 (読み仮名辞典 / アクセント辞典) を放送テキストへ適用した SSML を作り、Step2 へ表示する。
 */
export const CREATE_DICTIONARY_AUDIO_BUTTON_LABEL = '辞書適用Audio作成';

/**
 * Step3 の見出し (件数表記を除いた部分)。
 * 実際の見出しは `（最大 N 件）` を後ろに付ける ({@link MAX_SAMPLE_CANDIDATE_COUNT})。
 *
 * SBI-010: 旧「Step3 放送テキストのSSMLチューニング用Audioサンプル」から簡素化した
 * (責務の説明は作成ボタンの名称「SSMLタグでチューニングしたAudioサンプル作成」が担う)。
 */
export const STEP3_TITLE = 'Step3 Audioサンプル';
